import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface TaskItem {
  id: string;
  title: string;
  status: string;
  type: 'foundational' | 'growth' | 'recurring';
  time_spent?: number;
  assigned_to?: string;
  assigneeName?: string;
  tag_ids?: string[];
  [key: string]: any;
}

interface UserProfileData {
  id: string;
  name: string;
  alias: string;
  email: string;
  avatar?: string;
  type: string;
  role: string;
  tasks: {
    pending: number;
    in_progress: number;
    done: number;
    recent: TaskItem[];
  };
  createdTasks: {
    pending: number;
    in_progress: number;
    done: number;
    recent: TaskItem[];
  };
  recentActivity: Array<{
    id: string;
    action_type: string;
    user_action: string;
    created_at: string;
  }>;
}

export const useUserProfileData = (squadMemberId?: string, brandId?: string) => {
  return useQuery({
    queryKey: ['user-profile', squadMemberId, brandId],
    queryFn: async (): Promise<UserProfileData | null> => {
      if (!squadMemberId || !brandId) return null;

      // Try to get info from growth_squad first (with brand filter)
      let { data: squadMember } = await supabase
        .from('growth_squad')
        .select('id, name, alias, email, type, brand_id, user_id')
        .eq('id', squadMemberId)
        .eq('brand_id', brandId)
        .maybeSingle();

      // If not found in the specified brand, try without brand filter (cross-brand lookup)
      let effectiveBrandId = brandId;
      if (!squadMember) {
        const result = await supabase
          .from('growth_squad')
          .select('id, name, alias, email, type, brand_id, user_id')
          .eq('id', squadMemberId)
          .maybeSingle();
        
        squadMember = result.data;
        // Use the actual brand of the user for subsequent queries
        if (squadMember) {
          effectiveBrandId = squadMember.brand_id;
        }
      }

      // First check if user has brand access, but don't fail hover card if missing
      const { data: brandAccess, error: accessError } = await supabase
        .from('user_brand_access')
        .select('role, custom_role_id, custom_roles(name)')
        .eq('user_id', squadMemberId)
        .eq('brand_id', effectiveBrandId)
        .maybeSingle();

      // If not in growth_squad, fall back to profiles table
      let userName: string;
      let userAlias: string;
      let userEmail: string;
      let userType: string;
      let userAvatar: string | undefined;

      if (squadMember) {
        userName = squadMember.name;
        userAlias = squadMember.alias;
        userEmail = squadMember.email;
        userType = squadMember.type;
        
        // Fetch avatar from profiles if squad member has a user_id
        if (squadMember.user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('avatar_url')
            .eq('id', squadMember.user_id)
            .maybeSingle();
          
          if (profile?.avatar_url) {
            userAvatar = profile.avatar_url;
          } else {
            // Construct avatar URL from storage if profile avatar_url is null
            const supabaseUrl = 'https://hjodtxoylcvcwjgvuctk.supabase.co';
            userAvatar = `${supabaseUrl}/storage/v1/object/public/avatars/${squadMember.user_id}/avatar.png`;
          }
        }
      } else {
        // Fetch from profiles table
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, email, avatar_url')
          .eq('id', squadMemberId)
          .maybeSingle();

        if (profileError || !profile) {
          console.error('Error fetching user profile:', profileError);
          return null;
        }

        userName = profile.full_name || 'Unknown';
        userAlias = userName.split(' ')[0].toLowerCase();
        userEmail = profile.email || '';
        userType = 'Team Member';
        
        if (profile.avatar_url) {
          userAvatar = profile.avatar_url;
        } else {
          // Construct avatar URL from storage if profile avatar_url is null
          const supabaseUrl = 'https://hjodtxoylcvcwjgvuctk.supabase.co';
          userAvatar = `${supabaseUrl}/storage/v1/object/public/avatars/${squadMemberId}/avatar.png`;
        }
      }

      // Determine role (fall back gracefully if brandAccess is missing)
      let role = 'Member';
      if (brandAccess?.custom_role_id && brandAccess.custom_roles) {
        role = (brandAccess.custom_roles as any).name;
      } else if (brandAccess?.role) {
        role = brandAccess.role.charAt(0).toUpperCase() + brandAccess.role.slice(1);
      }

      // Fetch tasks ASSIGNED TO this member
      let foundationalTasks: any[] = [];
      let growthTasks: any[] = [];
      let recurringTasks: any[] = [];

      try {
        const fResult: any = await supabase
          .from('foundational_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        foundationalTasks = fResult.data || [];
      } catch (e) {}

      try {
        const gResult: any = await supabase
          .from('growth_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        growthTasks = gResult.data || [];
      } catch (e) {}

      try {
        const rResult: any = await supabase
          .from('recurring_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        recurringTasks = rResult.data || [];
      } catch (e) {}

      // Fetch tasks CREATED BY this member but assigned to OTHERS
      let createdFoundationalTasks: any[] = [];
      let createdGrowthTasks: any[] = [];
      let createdRecurringTasks: any[] = [];

      try {
        const cfResult: any = await supabase
          .from('foundational_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('user_id', squadMemberId)
          .not('assigned_to', 'is', null)
          .neq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        createdFoundationalTasks = cfResult.data || [];
      } catch (e) {}

      try {
        const cgResult: any = await supabase
          .from('growth_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('submitted_by', squadMemberId)
          .not('assigned_to', 'is', null)
          .neq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        createdGrowthTasks = cgResult.data || [];
      } catch (e) {}

      try {
        const crResult: any = await supabase
          .from('recurring_tasks')
          .select('*')
          .eq('brand_id', effectiveBrandId)
          .eq('user_id', squadMemberId)
          .not('assigned_to', 'is', null)
          .neq('assigned_to', squadMemberId)
          .order('updated_at', { ascending: false });
        createdRecurringTasks = crResult.data || [];
      } catch (e) {}

      // Collect all unique assigned_to IDs from created tasks
      const allCreatedRawTasks = [...createdFoundationalTasks, ...createdGrowthTasks, ...createdRecurringTasks];
      const assigneeIds = [...new Set(allCreatedRawTasks.map(t => t.assigned_to).filter(Boolean))];

      // Fetch squad members for assignee name resolution
      let squadMembers: any[] = [];
      if (assigneeIds.length > 0) {
        try {
          const { data: squadData } = await supabase
            .from('growth_squad')
            .select('id, name, alias')
            .in('id', assigneeIds);
          squadMembers = squadData || [];
        } catch (e) {}
      }

      // Create assignee lookup map
      const assigneeLookup = new Map(squadMembers.map(m => [m.id, m.alias || m.name]));

      // Combine tasks assigned to member
      const allTasks: TaskItem[] = [];

      foundationalTasks.forEach((t: any) => {
        allTasks.push({
          ...(t as any),
          id: t.id,
          title: t.title,
          status: t.status,
          type: 'foundational',
        });
      });

      growthTasks.forEach((t: any) => {
        allTasks.push({
          ...(t as any),
          id: t.id,
          title: t.name || t.title,
          status: t.task_status || t.status || 'pending',
          type: 'growth',
        });
      });

      recurringTasks.forEach((t: any) => {
        allTasks.push({
          ...(t as any),
          id: t.id,
          title: t.title,
          status: t.status,
          type: 'recurring',
        });
      });

      // Combine created tasks with assignee names
      const createdTasks: TaskItem[] = [];

      createdFoundationalTasks.forEach((t: any) => {
        createdTasks.push({
          ...(t as any),
          id: t.id,
          title: t.title,
          status: t.status,
          type: 'foundational',
          time_spent: t.time_spent,
          assigned_to: t.assigned_to,
          assigneeName: assigneeLookup.get(t.assigned_to) || 'Unknown',
        });
      });

      createdGrowthTasks.forEach((t: any) => {
        createdTasks.push({
          ...(t as any),
          id: t.id,
          title: t.name || t.title,
          status: t.task_status || t.status || 'pending',
          type: 'growth',
          time_spent: t.time_spent,
          assigned_to: t.assigned_to,
          assigneeName: assigneeLookup.get(t.assigned_to) || 'Unknown',
        });
      });

      createdRecurringTasks.forEach((t: any) => {
        createdTasks.push({
          ...(t as any),
          id: t.id,
          title: t.title,
          status: t.status,
          type: 'recurring',
          time_spent: t.time_spent,
          assigned_to: t.assigned_to,
          assigneeName: assigneeLookup.get(t.assigned_to) || 'Unknown',
        });
      });

      // Sort created tasks by updated_at
      createdTasks.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      // Calculate counts for assigned tasks
      const pending = allTasks.filter(t => t.status === 'pending' || t.status === 'to-do').length;
      const in_progress = allTasks.filter(t => t.status === 'in-progress' || t.status === 'ongoing').length;
      const done = allTasks.filter(t => t.status === 'done' || t.status === 'completed').length;

      // Calculate counts for created tasks
      const createdPending = createdTasks.filter(t => t.status === 'pending' || t.status === 'to-do').length;
      const createdInProgress = createdTasks.filter(t => t.status === 'in-progress' || t.status === 'ongoing').length;
      const createdDone = createdTasks.filter(t => t.status === 'done' || t.status === 'completed').length;

      // Fetch recent activity
      const { data: activities } = await supabase
        .from('activity_logs')
        .select('id, action_type, user_action, created_at')
        .eq('user_id', squadMemberId)
        .eq('brand_id', effectiveBrandId)
        .order('created_at', { ascending: false })
        .limit(5);

      return {
        id: squadMemberId,
        name: userName,
        alias: userAlias,
        email: userEmail,
        avatar: userAvatar,
        type: userType,
        role,
        tasks: {
          pending,
          in_progress,
          done,
          recent: allTasks,
        },
        createdTasks: {
          pending: createdPending,
          in_progress: createdInProgress,
          done: createdDone,
          recent: createdTasks,
        },
        recentActivity: activities || [],
      };
    },
    enabled: !!squadMemberId && !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
